import {
  type JSX,
  type Ref,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";

import { AlertDialog } from "./AlertDialog";
import {
  type AlertDialogDataProps,
  alertDialogDataSchema,
} from "./AlertDialog.schema";
import { AlertDialogAction } from "./AlertDialogAction";
import { AlertDialogCancel } from "./AlertDialogCancel";
import { AlertDialogContent } from "./AlertDialogContent";
import { AlertDialogDescription } from "./AlertDialogDescription";
import { AlertDialogFooter } from "./AlertDialogFooter";
import { AlertDialogHeader } from "./AlertDialogHeader";
import { AlertDialogTitle } from "./AlertDialogTitle";
import { ALERT_DIALOG_TAG_NAME } from "./constants";

export interface AlertDialogHandle {
  open: (originElement?: HTMLElement) => void;
  close: () => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [ALERT_DIALOG_TAG_NAME]: {
      props: AlertDialogDataProps;
      handle: AlertDialogHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-alert-dialog": HTMLElement & AlertDialogHandle;
  }
}

export function readAlertDialogSlotted(
  el: HTMLElement,
): Partial<AlertDialogDataProps> {
  return {
    onOpenChange: (open) => {
      if (open) {
        el.setAttribute("open", "");
      } else {
        el.removeAttribute("open");
      }

      el.dispatchEvent(
        new CustomEvent("alert-dialog:change", {
          detail: { open },
          bubbles: true,
        }),
      );
    },
    onConfirm: () => {
      el.dispatchEvent(
        new CustomEvent("alert-dialog:confirm", { bubbles: true }),
      );
    },
    onCancel: () => {
      el.dispatchEvent(
        new CustomEvent("alert-dialog:cancel", { bubbles: true }),
      );
    },
  };
}

function RWCAlertDialogComponent({
  ref,
  defaultOpen,
  open: controlledOpen,
  title,
  description,
  cancelLabel,
  actionLabel,
  variant,
  onOpenChange,
  onConfirm,
  onCancel,
}: Partial<AlertDialogDataProps> & {
  ref?: Ref<AlertDialogHandle>;
}): JSX.Element {
  const [open, setOpen] = useState(controlledOpen ?? defaultOpen ?? false);

  const originRef = useRef<DOMRect | null>(null);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  useImperativeHandle(
    ref,
    () => ({
      open: (originElement) => {
        if (originElement) {
          originRef.current = originElement.getBoundingClientRect();
        }

        handleOpenChange(true);
      },
      close: () => handleOpenChange(false),
    }),
    [handleOpenChange],
  );

  useLayoutEffect(() => {
    if (controlledOpen !== undefined) {
      setOpen(controlledOpen);
    }
  }, [controlledOpen]);

  return (
    <AlertDialog
      open={open}
      onOpenChange={handleOpenChange}
      originRef={originRef}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onCancel?.()}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={variant === "destructive" ? "destructive" : "primary"}
            onClick={() => onConfirm?.()}
          >
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

componentManager.register(
  rwc(ALERT_DIALOG_TAG_NAME, {
    component: RWCAlertDialogComponent,
    dataSchema: alertDialogDataSchema,
    usesSlots: true,
    readSlotted: readAlertDialogSlotted,
  }),
);
